import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getVerChamado, changeOpenDetalhesChamado } from 'src/redux/slices/chamados';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Input } from 'src/@/components/ui/input';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { formatDistanceToNowStrict } from 'date-fns';
import getDateLocale from 'src/utils/getDateLocale';
import { Avatar, AvatarFallback } from 'src/@/components/ui/avatar';
import { cn } from 'src/@/lib/utils';
import { Button } from 'src/@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from 'src/@/components/ui/dropdown-menu';

const STATUS_TABS = [
  { value: 'pendente', label: 'Pendentes' },
  { value: 'aberto', label: 'Abertos' },
  { value: 'recusado', label: 'Fechados' },
];

const SORT_OPTIONS = [
  { value: 'last_activity', label: 'Ultima atividade' },
  { value: 'created', label: 'Data de criação' },
  { value: 'priority', label: 'Prioridade' },
];

const TAB_COLORS = {
  'pendente': { bg: 'bg-amber-500', text: 'text-amber-600' },
  'aberto': { bg: 'bg-violet-500', text: 'text-violet-600' },
  'recusado': { bg: 'bg-emerald-500', text: 'text-emerald-600' },
};

const AVATAR_COLORS = [
  'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) initials += names[names.length - 1].substring(0, 1).toUpperCase();
  return initials;
}

export default function ChamadosSidebar({ onSelectTicket, handleOpenNovoChamado, onNavigate, currentView = "detalhes", listaCaixas = [], caixaSelecionada = '', onChangeCaixa }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { colunas, tickets, chamado } = useSelector((state) => state.chamados);

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('pendente');
  const [sortBy, setSortBy] = useState('last_activity');

  const getFilteredTickets = () => {
    if (!colunas || !colunas[activeTab]) return [];
    if (!tickets) return [];

    const column = colunas[activeTab];
    const ticketIds = column.ticketsId || [];

    let ticketList = ticketIds
      .map(id => tickets[id])
      .filter(ticket => ticket);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      ticketList = ticketList.filter(ticket =>
        ticket.titulo?.toLowerCase().includes(query) ||
        ticket.chamado?.toString().includes(query) ||
        ticket.nome_autor?.toLowerCase().includes(query)
      );
    }

    ticketList.sort((a, b) => {
      if (sortBy === 'last_activity') {
        return new Date(b.ultima_modificacao || b.data_criacao) - new Date(a.ultima_modificacao || a.data_criacao);
      }
      if (sortBy === 'created') {
        return new Date(b.data_criacao) - new Date(a.data_criacao);
      }
      return 0;
    });

    return ticketList;
  };

  const getTicketCount = (tabName) => {
    if (!colunas || !colunas[tabName]) return 0;
    if (!tickets) return 0;
    const column = colunas[tabName];
    const ticketIds = column.ticketsId || [];
    return ticketIds.filter(id => tickets[id]).length;
  };

  const totalOpen = useMemo(() => {
    return getTicketCount(activeTab);
  }, [colunas, tickets, activeTab]);

  const handleTicketClick = (ticketId) => {
    dispatch(getVerChamado(ticketId));
    dispatch(changeOpenDetalhesChamado(false));
    if (onSelectTicket) onSelectTicket(ticketId);
  };

  const filteredTickets = getFilteredTickets();

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-[#1A1D21]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-[52px] border-b border-gray-100 dark:border-neutral-800 flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-lg px-1.5 py-1 transition-colors -ml-1.5">
              <div className="w-7 h-7 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                <Iconify icon="mdi:ticket-confirmation" width={14} className="text-violet-600 dark:text-violet-400" />
              </div>
              <span className="text-[14px] font-bold text-gray-900 dark:text-gray-100 truncate max-w-[140px]">
                {caixaSelecionada
                  ? listaCaixas.find(c => c._id === caixaSelecionada)?.nome || 'Todos'
                  : 'Todos'}
              </span>
              <Iconify icon="solar:alt-arrow-down-linear" width={14} className="text-gray-400 flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem
              onClick={() => onChangeCaixa?.('')}
              className={cn("text-sm", !caixaSelecionada && "font-semibold text-violet-600")}
            >
              <Iconify icon="mdi:ticket-confirmation" width={16} className="mr-2 text-violet-500" />
              Todos os chamados
            </DropdownMenuItem>
            {listaCaixas.length > 0 && <DropdownMenuSeparator />}
            {listaCaixas.map((caixa) => (
              <DropdownMenuItem
                key={caixa._id}
                onClick={() => onChangeCaixa?.(caixa._id)}
                className={cn("text-sm", caixaSelecionada === caixa._id && "font-semibold text-violet-600")}
              >
                <Iconify icon="mdi:inbox" width={16} className="mr-2 text-gray-400" />
                {caixa.nome}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex items-center gap-1">
          {onNavigate && (
            <>
              <Button
                onClick={() => onNavigate('/dashboard/chamados')}
                title="Kanban"
                size="icon"
                variant="ghost"
                className="w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <Iconify icon="mdi:view-dashboard" width={15} />
              </Button>
              <Button
                onClick={() => onNavigate('/dashboard/meus-chamados')}
                title="Lista"
                size="icon"
                variant="ghost"
                className="w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <Iconify icon="mdi:format-list-bulleted" width={15} />
              </Button>
            </>
          )}
          <Button
            onClick={() => setShowSearch(!showSearch)}
            size="icon"
            variant="ghost"
            className={cn(
              "w-7 h-7 rounded-lg",
              showSearch
                ? "bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            )}
          >
            <Iconify icon="eva:search-outline" width={16} />
          </Button>
          {handleOpenNovoChamado && (
            <Button
              onClick={handleOpenNovoChamado}
              title="Novo Chamado"
              size="icon"
              className="w-7 h-7 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
            >
              <Iconify icon="mdi:plus" width={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Search (toggle) */}
      {showSearch && (
        <div className="px-3 pt-3 pb-1 animate-in fade-in slide-in-from-top-2 duration-200">
          <Input
            autoFocus
            placeholder="Pesquisar chamados..."
            className="h-8 bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 rounded-lg text-sm focus-visible:ring-0 focus-visible:border-gray-300 shadow-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Underline Tabs + Sort */}
      <div className="flex items-center border-b border-gray-200 dark:border-neutral-800 mx-3">
        <div className="flex flex-1 min-w-0 overflow-hidden">
          {STATUS_TABS.map((tab) => {
            const isActive = activeTab === tab.value;
            const count = getTicketCount(tab.value);
            const colors = TAB_COLORS[tab.value];
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  "relative flex-1 flex items-center justify-center gap-1 px-1 py-2.5 text-[12px] transition-colors border-b-2 -mb-px whitespace-nowrap",
                  isActive
                    ? "border-violet-500 text-gray-900 dark:text-white font-semibold"
                    : "border-transparent text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-300 font-medium"
                )}
              >
                {tab.label}
                {count > 0 && (
                  <span className={cn(
                    "min-w-[14px] h-[14px] flex items-center justify-center rounded-full px-0.5 text-[8px] font-bold leading-none",
                    isActive
                      ? `${colors.bg} text-white`
                      : "bg-gray-200 dark:bg-neutral-700 text-gray-500 dark:text-neutral-400"
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="flex-shrink-0 text-[10px] text-gray-400 dark:text-neutral-500 bg-transparent border-0 outline-none cursor-pointer py-0 appearance-auto ml-1"
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Ticket List */}
      <ScrollArea className="flex-1">
        <div className="flex flex-col">
          {filteredTickets.map((ticket) => {
            const isSelected = chamado?._id === ticket._id;
            const timeAgo = (ticket.ultima_modificacao || ticket.data_criacao)
              ? formatDistanceToNowStrict(new Date(ticket.ultima_modificacao || ticket.data_criacao), { locale: getDateLocale() })
              : '';

            return (
              <div
                key={ticket._id}
                onClick={() => handleTicketClick(ticket._id)}
                className={cn(
                  "group relative px-3 py-2 border-b border-gray-100 dark:border-neutral-800 cursor-pointer transition-colors",
                  isSelected
                    ? "bg-violet-50/40 dark:bg-violet-500/5 border-l-[3px] border-l-violet-500"
                    : "hover:bg-gray-50 dark:hover:bg-neutral-800/50 border-l-[3px] border-l-transparent"
                )}
              >
                <div>
                  <div className="min-w-0">
                    {/* Row 1: Ticket number + Type badge + Time */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-bold font-mono text-violet-600 dark:text-violet-400 flex-shrink-0">
                          #{ticket.chamado}
                        </span>
                        <span className={cn(
                          "text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0",
                          ticket.tipo === 'externo'
                            ? "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"
                            : "bg-gray-100 text-gray-500 dark:bg-neutral-800 dark:text-neutral-400"
                        )}>
                          {ticket.tipo === 'externo' ? 'Externo' : 'Interno'}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-400 dark:text-neutral-500 whitespace-nowrap flex-shrink-0">
                        {timeAgo}
                      </span>
                    </div>

                    {/* Row 2: Title */}
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate mt-0.5">
                      {ticket.titulo}
                    </p>

                    {/* Row 3: Author + Priority */}
                    <div className="flex items-center justify-between gap-1 mt-0.5">
                      <span className="text-[11px] text-gray-400 dark:text-neutral-500 truncate">
                        {ticket.nome_autor}
                      </span>
                      {ticket.prioridade_titulo && (
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: ticket.prioridade_cor || '#7C3AED' }}
                          />
                          <span className="text-[10px] font-medium text-gray-500 dark:text-neutral-400">
                            {ticket.prioridade_titulo}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredTickets.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-neutral-800 flex items-center justify-center mb-3 text-gray-300 dark:text-neutral-600">
                <Iconify icon="mdi:ticket-outline" width={22} />
              </div>
              <p className="text-sm font-medium text-gray-400">Nenhum chamado encontrado</p>
              <p className="text-xs text-gray-300 dark:text-neutral-600 mt-1">Tente outra aba ou pesquisa</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
